/**
 * PA Header — catalog-style multi-row header with megamenu + mobile drawer.
 *
 * Plain custom element (no theme Component dependency). Cart/search actions
 * reuse the theme's global `on:click` delegation. This file handles sticky
 * state (including .page-wrapper scroll on desktop), utility-row collapse,
 * megamenu open/close, mobile drawer with focus trap, and header height CSS
 * variables.
 */

const DESKTOP_MEDIA = '(min-width: 990px)';
const CATALOG_AIM_HISTORY_MS = 80;
const CATALOG_AIM_MAX_DELAY_MS = 300;
const CATALOG_AIM_PAD_PX = 8;
const CATALOG_AIM_LOOKAHEAD_PX = 800;
const CATALOG_RESIZE_MS = 260;
const CATALOG_FADE_MS = 180;
const CATALOG_EASING = 'cubic-bezier(0.2, 0.7, 0.3, 1)';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class PaHeader extends HTMLElement {
  #openName = null;
  #scrollRaf = 0;
  #media = window.matchMedia(DESKTOP_MEDIA);
  #resizeObserver = null;
  #scrollTarget = null;
  #focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  #previouslyFocused = null;
  #onDrawerKeydown = null;
  #catalogAimCancels = [];
  #catalogAbort = null;

  connectedCallback() {
    this.#bindSticky();
    this.#bindMegamenu();
    this.#bindCatalog();
    this.#bindDrawer();
    this.#setHeightVars();

    this.#resizeObserver = new ResizeObserver(() => this.#setHeightVars());
    this.#resizeObserver.observe(this);

    this.#media.addEventListener('change', this.#onMediaChange);
  }

  disconnectedCallback() {
    this.#unbindScroll();
    document.removeEventListener('click', this.#onDocumentClick);
    this.#media.removeEventListener('change', this.#onMediaChange);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollRaf);
    this.#cancelCatalogAim();
    this.#catalogAbort?.abort();
    if (this.#onDrawerKeydown) {
      this.removeEventListener('keydown', this.#onDrawerKeydown);
    }
  }

  #getScrollTarget() {
    if (this.#media.matches) {
      return document.querySelector('.page-wrapper') || window;
    }
    return window;
  }

  #getScrollY() {
    const target = this.#scrollTarget;
    if (!target || target === window) return window.scrollY || document.documentElement.scrollTop || 0;
    return target.scrollTop || 0;
  }

  #setHeightVars() {
    const headerGroup = document.querySelector('#header-group');
    let groupHeight = 0;
    if (headerGroup) {
      for (const child of headerGroup.children) {
        if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
      }
    }
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);

    if (this.hasAttribute('data-mega-open')) {
      this.style.setProperty('--pa-overlay-top', `${this.getBoundingClientRect().bottom}px`);
    }
  }

  #bindSticky() {
    this.#bindScroll();
    this.#media.addEventListener('change', this.#rebindScroll);
  }

  #rebindScroll = () => {
    this.#bindScroll();
    this.#onScroll();
  };

  #bindScroll() {
    this.#unbindScroll();
    this.#scrollTarget = this.#getScrollTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#onScroll();
  }

  #unbindScroll() {
    if (!this.#scrollTarget) return;
    this.#scrollTarget.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
  }

  #onScroll = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      const stuck = this.#getScrollY() > 4;
      this.toggleAttribute('data-stuck', stuck);
      this.querySelector('.pa-header__utility')?.toggleAttribute('inert', stuck);
    });
  };

  #onMediaChange = () => {
    this.#closeMega();
    this.#closeDrawer();
  };

  #bindMegamenu() {
    const items = this.querySelectorAll('[data-mega-item]');

    for (const item of items) {
      const trigger = item.querySelector('[data-mega-trigger]');
      if (!trigger) continue;

      const panel =
        item.querySelector('[data-mega-panel]') ??
        this.querySelector(`[data-mega-panels] > [data-mega-panel="${trigger.dataset.megaTrigger}"]`);
      if (!panel) continue;

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.#openName === trigger.dataset.megaTrigger) {
          this.#closeMega();
        } else {
          this.#open(trigger, panel);
        }
      });
    }

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#openName) return;
      const trigger = this.querySelector(`[data-mega-trigger="${this.#openName}"]`);
      this.#closeMega();
      trigger?.focus();
    });

    document.addEventListener('click', this.#onDocumentClick);

    this.querySelector('[data-mega-overlay]')?.addEventListener('click', () => {
      this.#closeMega();
    });
  }

  #cancelCatalogAim() {
    for (const cancel of this.#catalogAimCancels) cancel();
  }

  #bindCatalog() {
    this.#cancelCatalogAim();
    this.#catalogAbort?.abort();
    this.#catalogAbort = new AbortController();
    const { signal } = this.#catalogAbort;
    this.#catalogAimCancels = [];

    for (const catalog of this.querySelectorAll('[data-mega-catalog]')) {
      const tabs = [...catalog.querySelectorAll('[role="tab"]')];
      if (tabs.length === 0) continue;

      const panels = catalog.querySelector('.pa-mega-catalog__panels');
      /** @type {{x: number, y: number, t: number}[]} */
      const pointerTrail = [];
      /** @type {{x: number, y: number} | null} */
      let aimOrigin = null;
      /** @type {HTMLElement | null} */
      let pendingTab = null;
      let pendingTimer = 0;

      const panelFor = (tab) =>
        catalog.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`);

      const selectedTab = () => tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? tabs[0];

      const tabFromTarget = (target) =>
        target instanceof Element ? target.closest('[role="tab"]') : null;

      const clearAimTimers = () => {
        window.clearTimeout(pendingTimer);
        pendingTimer = 0;
        pendingTab = null;
      };

      const recordPointer = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return null;
        const point = { x: event.clientX, y: event.clientY, t: performance.now() };
        pointerTrail.push(point);
        const cutoff = point.t - CATALOG_AIM_HISTORY_MS;
        while (pointerTrail.length > 1 && pointerTrail[0].t < cutoff) pointerTrail.shift();
        if (pointerTrail.length > 8) pointerTrail.shift();
        return point;
      };

      /** @type {Animation | null} */
      let resizeAnim = null;

      const swapPanels = (apply, nextPanel) => {
        if (!panels || REDUCED_MOTION.matches || panels.offsetHeight === 0) {
          apply();
          return;
        }

        const from = panels.getBoundingClientRect().height;
        resizeAnim?.cancel();
        resizeAnim = null;
        apply();
        const to = panels.getBoundingClientRect().height;

        nextPanel?.animate(
          [
            { opacity: 0, transform: 'translateY(4px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: CATALOG_FADE_MS, easing: 'ease-out' }
        );

        if (Math.abs(to - from) < 1) {
          panels.style.overflow = '';
          return;
        }

        panels.style.overflow = 'clip';
        const anim = panels.animate([{ height: `${from}px` }, { height: `${to}px` }], {
          duration: CATALOG_RESIZE_MS,
          easing: CATALOG_EASING,
        });
        resizeAnim = anim;
        anim.addEventListener('finish', () => {
          if (resizeAnim !== anim) return;
          resizeAnim = null;
          panels.style.overflow = '';
        });
      };

      const select = (tab, { focus = false } = {}) => {
        clearAimTimers();
        aimOrigin = pointerTrail[pointerTrail.length - 1] ?? aimOrigin;
        const apply = () => {
          for (const item of tabs) {
            const selected = item === tab;
            item.setAttribute('aria-selected', String(selected));
            item.tabIndex = selected ? 0 : -1;
            const panel = panelFor(item);
            if (!panel) continue;
            panel.toggleAttribute('hidden', !selected);
            panel.toggleAttribute('data-open', selected);
          }
        };
        if (tab.getAttribute('aria-selected') === 'true') {
          apply();
        } else {
          swapPanels(apply, panelFor(tab));
        }
        if (focus) tab.focus();
      };

      const commitPending = () => {
        if (!pendingTab || pendingTab.getAttribute('aria-selected') === 'true') {
          clearAimTimers();
          return;
        }
        select(pendingTab);
      };

      const isMouseAimingAtPanel = (pointer) => {
        const current = selectedTab();
        const panel = current ? panelFor(current) : null;
        const to = pointer ?? pointerTrail[pointerTrail.length - 1];
        if (!panel || !to) return false;
        const from = pointerTrail[0] ?? aimOrigin ?? to;
        return isAimingAtCatalogPanel(aimOrigin, from, to, panel);
      };

      const scheduleTab = (tab) => {
        pendingTab = tab;
        window.clearTimeout(pendingTimer);
        pendingTimer = window.setTimeout(commitPending, CATALOG_AIM_MAX_DELAY_MS);
      };

      const trackPointer = (event) => {
        const point = recordPointer(event);
        if (!point) return;

        const current = selectedTab();
        const hoveredTab = tabFromTarget(event.target);
        if (hoveredTab === current) aimOrigin = point;

        if (!pendingTab) return;
        if (isMouseAimingAtPanel(point)) {
          if (!hoveredTab) {
            clearAimTimers();
            return;
          }
          scheduleTab(pendingTab);
          return;
        }
        commitPending();
      };

      const onTabPointerEnter = (event, tab) => {
        if (event.pointerType !== 'mouse') return;
        const point = recordPointer(event);
        const active = document.activeElement;
        if (
          active instanceof HTMLElement &&
          catalog.contains(active) &&
          active.getAttribute('role') !== 'tab'
        ) {
          return;
        }
        if (tab.getAttribute('aria-selected') === 'true') {
          clearAimTimers();
          aimOrigin = point ?? aimOrigin;
          return;
        }
        if (isMouseAimingAtPanel(point)) {
          scheduleTab(tab);
          return;
        }
        select(tab);
      };

      catalog.addEventListener('pointermove', trackPointer, { passive: true, signal });
      catalog.addEventListener('pointerleave', clearAimTimers, { signal });
      panels?.addEventListener('pointerenter', clearAimTimers, { signal });

      for (const tab of tabs) {
        tab.addEventListener('click', () => select(tab), { signal });
        tab.addEventListener('pointerenter', (event) => onTabPointerEnter(event, tab), { signal });
        tab.addEventListener('keydown', (event) => {
          const index = tabs.indexOf(tab);
          let next = -1;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            next = (index + 1) % tabs.length;
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            next = (index - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            next = 0;
          } else if (event.key === 'End') {
            next = tabs.length - 1;
          }
          if (next < 0) return;
          event.preventDefault();
          select(tabs[next], { focus: true });
        }, { signal });
      }

      this.#catalogAimCancels.push(clearAimTimers);
    }
  }

  #onDocumentClick = (event) => {
    if (!this.#openName || !(event.target instanceof Node)) return;
    const inTrigger = event.target.closest?.('[data-mega-trigger]');
    const inPanel = event.target.closest?.('[data-mega-panel]');
    if (inTrigger || inPanel) return;
    this.#closeMega();
  };

  #open(trigger, panel) {
    if (this.#openName === trigger.dataset.megaTrigger) return;
    this.#closeMega();

    this.#openName = trigger.dataset.megaTrigger;
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('data-open', '');
    this.style.setProperty('--pa-overlay-top', `${this.getBoundingClientRect().bottom}px`);
    this.setAttribute('data-mega-open', '');
  }

  #closeMega() {
    if (!this.#openName) return;
    const trigger = this.querySelector(`[data-mega-trigger="${this.#openName}"]`);
    const panel = this.querySelector(`[data-mega-panel="${this.#openName}"][data-open]`);

    trigger?.setAttribute('aria-expanded', 'false');
    panel?.removeAttribute('data-open');
    this.removeAttribute('data-mega-open');
    this.#openName = null;
    this.#cancelCatalogAim();
  }

  #bindDrawer() {
    const toggles = this.querySelectorAll('[data-drawer-toggle]');
    const backdrop = this.querySelector('[data-drawer-backdrop]');
    const drawer = this.querySelector('[data-drawer]');

    for (const toggle of toggles) {
      toggle.addEventListener('click', () => {
        if (this.hasAttribute('data-drawer-open')) {
          this.#closeDrawer();
        } else {
          this.#openDrawer();
        }
      });
    }

    backdrop?.addEventListener('click', () => this.#closeDrawer());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-drawer-open')) {
        this.#closeDrawer();
      }
    });

    for (const accordionToggle of this.querySelectorAll('[data-accordion-toggle]')) {
      accordionToggle.addEventListener('click', () => {
        const accordion = accordionToggle.closest('[data-accordion]');
        if (!accordion) return;
        const isOpen = accordion.hasAttribute('data-open');
        accordion.toggleAttribute('data-open', !isOpen);
        accordionToggle.setAttribute('aria-expanded', String(!isOpen));
      });
    }

    this.#onDrawerKeydown = (event) => {
      if (!this.hasAttribute('data-drawer-open') || event.key !== 'Tab' || !drawer) return;
      const focusable = [...drawer.querySelectorAll(this.#focusableSelector)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    this.addEventListener('keydown', this.#onDrawerKeydown);
  }

  #openDrawer() {
    const drawer = this.querySelector('[data-drawer]');
    this.#previouslyFocused = document.activeElement;
    this.setAttribute('data-drawer-open', '');
    document.body.style.overflow = 'hidden';
    document.documentElement.setAttribute('scroll-lock', '');
    this.querySelectorAll('[data-drawer-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'true');
    });

    // Inert main/footer so focus cannot escape behind the drawer
    document.querySelector('main')?.setAttribute('inert', '');
    document.querySelector('footer')?.setAttribute('inert', '');
    document.querySelector('#header-group')?.querySelectorAll('.shopify-section').forEach((section) => {
      if (!section.contains(this)) section.setAttribute('inert', '');
    });

    // Defer focus until after visibility/transform paint so browsers accept it.
    window.setTimeout(() => {
      if (!this.hasAttribute('data-drawer-open')) return;
      const firstFocusable =
        drawer?.querySelector('.pa-drawer__search-input') ||
        drawer?.querySelector(this.#focusableSelector);
      firstFocusable?.focus({ preventScroll: true });
    }, 50);
  }

  /**
   * Public close for overlay drawers opened from the mobile nav (e.g. BoM).
   */
  closeMobileDrawer() {
    this.#closeDrawer();
  }

  #closeDrawer() {
    if (!this.hasAttribute('data-drawer-open')) return;
    this.removeAttribute('data-drawer-open');
    document.body.style.overflow = '';
    document.documentElement.removeAttribute('scroll-lock');
    this.querySelectorAll('[data-drawer-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });

    document.querySelector('main')?.removeAttribute('inert');
    document.querySelector('footer')?.removeAttribute('inert');
    document.querySelector('#header-group')?.querySelectorAll('[inert]').forEach((el) => {
      el.removeAttribute('inert');
    });

    if (this.#previouslyFocused instanceof HTMLElement) {
      this.#previouslyFocused.focus();
    } else {
      this.querySelector('[data-drawer-toggle]')?.focus();
    }
    this.#previouslyFocused = null;
  }
}

if (!customElements.get('pa-header')) {
  customElements.define('pa-header', PaHeader);
}

/**
 * @param {{x: number, y: number}} p
 * @param {{x: number, y: number}} a
 * @param {{x: number, y: number}} b
 * @param {{x: number, y: number}} c
 */
function pointInTriangle(p, a, b, c) {
  const dX = p.x - c.x;
  const dY = p.y - c.y;
  const dX21 = c.x - b.x;
  const dY12 = b.y - c.y;
  const den = dY12 * (a.x - c.x) + dX21 * (a.y - c.y);
  if (den === 0) return false;
  const s = dY12 * dX + dX21 * dY;
  const t = (c.y - a.y) * dX + (a.x - c.x) * dY;
  if (den > 0) return s >= 0 && t >= 0 && s + t <= den;
  return s <= 0 && t <= 0 && s + t >= den;
}

/**
 * @param {{x: number, y: number}} from
 * @param {{x: number, y: number}} to
 * @param {DOMRectReadOnly} rect
 */
function rayHitsRect(from, to, rect) {
  const vx = to.x - from.x;
  const vy = to.y - from.y;
  const speed = Math.hypot(vx, vy);
  if (speed < 1) return false;

  const left = rect.left - CATALOG_AIM_PAD_PX;
  const right = rect.right + CATALOG_AIM_PAD_PX;
  const top = rect.top - CATALOG_AIM_PAD_PX;
  const bottom = rect.bottom + CATALOG_AIM_PAD_PX;
  let tMin = 0;
  let tMax = CATALOG_AIM_LOOKAHEAD_PX / speed;

  if (Math.abs(vx) < 0.01) {
    if (from.x < left || from.x > right) return false;
  } else {
    let t1 = (left - from.x) / vx;
    let t2 = (right - from.x) / vx;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return false;
  }

  if (Math.abs(vy) < 0.01) {
    if (from.y < top || from.y > bottom) return false;
  } else {
    let t1 = (top - from.y) / vy;
    let t2 = (bottom - from.y) / vy;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1);
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return false;
  }

  return tMax >= tMin;
}

/**
 * @param {{x: number, y: number}} p
 * @param {{left: number, right: number, top: number, bottom: number}} rect
 */
function distanceToRect(p, rect) {
  const dx = Math.max(rect.left - p.x, 0, p.x - rect.right);
  const dy = Math.max(rect.top - p.y, 0, p.y - rect.bottom);
  return Math.hypot(dx, dy);
}

/**
 * @param {{x: number, y: number} | null} origin
 * @param {{x: number, y: number}} from
 * @param {{x: number, y: number}} to
 * @param {Element} panel
 */
function isAimingAtCatalogPanel(origin, from, to, panel) {
  const rect = panel.getBoundingClientRect();
  const padded = {
    left: rect.left - CATALOG_AIM_PAD_PX,
    right: rect.right + CATALOG_AIM_PAD_PX,
    top: rect.top - CATALOG_AIM_PAD_PX,
    bottom: rect.bottom + CATALOG_AIM_PAD_PX,
  };

  if (to.x >= padded.left && to.x <= padded.right && to.y >= padded.top && to.y <= padded.bottom) {
    return true;
  }

  if (distanceToRect(to, padded) > distanceToRect(from, padded) + 1) {
    return false;
  }

  if (origin) {
    const panelOnRight = rect.left + rect.width / 2 >= origin.x;
    const nearX = panelOnRight ? rect.left : rect.right;
    if (pointInTriangle(to, origin, { x: nearX, y: padded.top }, { x: nearX, y: padded.bottom })) {
      return true;
    }
  }

  return rayHitsRect(from, to, rect);
}
